// Modules visibles du niveau 250, dessinés sur le tank pour tous les joueurs (repère de l'art : rayon ART_R,
// nez de la caisse vers +x). La hitbox ne change jamais : blindage et propulseurs dépassent un peu de la caisse.
import { Graphics } from 'pixi.js';
import { ART_R } from './art';

/** Bits des modules (identiques à shared/catalog/modules.ts). */
export const MOD_BIT = { shield: 1, turret: 2, drones: 4, armor: 8, thrusters: 16, radar: 32 } as const;

const STEEL = 0x56606c;
const STEEL_DARK = 0x323941;
const STEEL_LIGHT = 0x7d8996;
const INK = 0x14181d;
const GLOW = 0x7fe3ff;

/** Hexagone (anneau facetté du bouclier de module). */
export function hexRing(r: number): number[] {
  const pts: number[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
    pts.push(Math.cos(a) * r, Math.sin(a) * r);
  }
  return pts;
}

interface ModuleGraphics {
  modHull: Graphics;
  modTurret: Graphics;
  modRadar: Graphics;
}

/** Redessine les pièces des modules ; la flamme (propulseurs) et l'antenne (radar) sont des enfants animés. */
export function drawModules(v: ModuleGraphics, mask: number): void {
  const R = ART_R;
  for (const g of [v.modHull, v.modTurret, v.modRadar]) {
    for (const c of g.removeChildren()) c.destroy();
    g.clear();
  }
  const h = v.modHull;
  if (mask & MOD_BIT.thrusters) {
    // Flamme d'abord (enfant 0, sous les tuyères) : son opacité suit la vitesse.
    const flame = new Graphics();
    for (const side of [-1, 1]) {
      const y = side * 0.32 * R;
      flame.poly([-0.92 * R, y - 0.13 * R, -1.45 * R, y, -0.92 * R, y + 0.13 * R]).fill({ color: 0xff9a3d, alpha: 0.85 });
      flame.poly([-0.92 * R, y - 0.07 * R, -1.22 * R, y, -0.92 * R, y + 0.07 * R]).fill({ color: 0xfff0b0, alpha: 0.95 });
    }
    flame.alpha = 0;
    h.addChild(flame);
    for (const side of [-1, 1]) {
      const y = side * 0.32 * R;
      h.roundRect(-0.96 * R, y - 0.17 * R, 0.36 * R, 0.34 * R, 0.06 * R).fill(STEEL).stroke({ width: 1.8, color: INK });
      h.rect(-0.98 * R, y - 0.11 * R, 0.06 * R, 0.22 * R).fill(GLOW);
    }
  }
  if (mask & MOD_BIT.armor) {
    // Plaques épaisses, plus sombres que la caisse : un éperon sur le nez, deux jupes sur les flancs.
    h.poly([0.58 * R, -0.62 * R, 0.98 * R, -0.3 * R, 0.98 * R, 0.3 * R, 0.58 * R, 0.62 * R, 0.7 * R, 0, 0.58 * R, -0.62 * R]).fill(STEEL_DARK).stroke({ width: 2.5, color: STEEL_LIGHT });
    for (const side of [-1, 1]) {
      h.roundRect(-0.72 * R, side * 0.9 * R - 0.15 * R, 1.38 * R, 0.3 * R, 0.07 * R).fill(STEEL_DARK).stroke({ width: 2.5, color: STEEL_LIGHT });
      for (const x of [-0.5, -0.17, 0.17, 0.5]) h.circle(x * R, side * 0.9 * R, 0.045 * R).fill(GLOW);
    }
    for (const y of [-0.3, 0, 0.3]) h.circle(0.84 * R, y * R, 0.045 * R).fill(GLOW);
  }
  if (mask & MOD_BIT.drones) {
    // Soute des drones d'escorte : deux trappes lumineuses aux coins arrière, hors de la tourelle.
    for (const side of [-1, 1]) {
      h.roundRect(-0.74 * R, side * 0.5 * R - 0.15 * R, 0.3 * R, 0.3 * R, 0.06 * R).fill(INK).stroke({ width: 2, color: GLOW });
      h.poly([-0.68 * R, side * 0.5 * R, -0.5 * R, side * 0.5 * R - 0.08 * R, -0.5 * R, side * 0.5 * R + 0.08 * R]).fill(GLOW);
    }
  }
  if (mask & MOD_BIT.turret) {
    // Tourelle du module : couronne lumineuse autour de la tourelle principale et long tube fin (repère autoAim).
    const t = v.modTurret;
    t.circle(0, 0, 0.66 * R).stroke({ width: 4, color: STEEL_LIGHT }).circle(0, 0, 0.66 * R).stroke({ width: 1.5, color: GLOW, alpha: 0.9 });
    t.roundRect(0.5 * R, -0.15 * R, 1.05 * R, 0.3 * R, 0.07 * R).fill(STEEL_LIGHT).stroke({ width: 2, color: INK });
    t.rect(1.38 * R, -0.15 * R, 0.1 * R, 0.3 * R).fill(GLOW);
    t.circle(0.66 * R, 0, 0.2 * R).fill(STEEL).stroke({ width: 2, color: INK });
    t.circle(0.66 * R, 0, 0.08 * R).fill(GLOW);
  }
  if (mask & MOD_BIT.radar) {
    // Radar : mât à l'arrière de la caisse, hors de la tourelle, antenne qui balaie (enfant 0).
    const r = v.modRadar;
    r.circle(-0.72 * R, 0, 0.17 * R).fill(STEEL).stroke({ width: 2, color: INK });
    const dish = new Graphics();
    dish.arc(0, 0, 0.36 * R, -1, 1).stroke({ width: 4, color: STEEL_LIGHT });
    dish.arc(0, 0, 0.36 * R, -1, 1).stroke({ width: 1.5, color: GLOW });
    dish.moveTo(0, 0).lineTo(0.36 * R, 0).stroke({ width: 2, color: GLOW });
    dish.circle(0, 0, 0.07 * R).fill(GLOW);
    dish.position.set(-0.72 * R, 0);
    r.addChildAt(dish, 0);
  }
}
